// App.js
import React, { useState, useEffect, lazy, Suspense } from 'react';
import { useAuthenticatedUserInfo } from './services/Contexts/AuthenticatedUserInfoContext';
import { useSubscription } from './hooks/useSubscription';
import './index.css';
import LoadingSpinner from './components/LoadingSpinner/LoadingSpinner';
import ScrollToTopRef from './components/ScrollToTopRef';
import LoggedOutLayout from './components/layout/LoggedOutLayout';
import LoggedInLayout from './components/layout/LoggedInLayout';
import AppRoutes from './AppRoutes';
import { usePageTitleContext } from './services/Contexts/PageTitleContext';
import { useLocation, Navigate } from 'react-router-dom';
import SubscribeToPlanFirstTimePage from './pages/Subscription/SubscribeToPlanFirstTimePage';
import { useFeatureGate } from './services/Contexts/FeatureGateContext';
import FeatureGatePage from './components/Reusables/FeatureGatePage/FeatureGatePage';
import { getDashboardPathForRole } from './utils/dashboardNavigation';
import { isGuestOnlyPath } from './components/RouteComponents';
import { hasAppSumoFlow } from './utils/appsumoSession';
import {
	buildAuthenticatedHostRedirectUrl,
	shouldRedirectPortalGuestToAuth,
	isClientPortalPath,
} from './utils/subdomainUtils';
import PlatformStatusBanner from './components/PlatformStatusBanner/PlatformStatusBanner';
import { resolveCrossAppRoleRedirectUrl } from './utils/crossAppUrls';

const ClientPortalApp = lazy(() => import('./ClientPortalApp'));

const App = () => {
	const { userId, token, userRole, companyId, loading, company } =
		useAuthenticatedUserInfo();
	const { data: subscription, isLoading: subscriptionLoading } =
		useSubscription(companyId);
	const [isLoading, setIsLoading] = useState(true);
	const location = useLocation();
	const { getPageTitle, getPageDescription } = usePageTitleContext();
	const pageTitle = getPageTitle(location.pathname) || '';
	const pageDescription = getPageDescription(location.pathname) || '';
	const { isOpen: isFeatureGated, close: closeFeatureGate } = useFeatureGate();

	// Clear feature gate when user navigates to a different page
	const prevPathnameRef = React.useRef(location.pathname);
	React.useEffect(() => {
		if (location.pathname !== prevPathnameRef.current) {
			prevPathnameRef.current = location.pathname;
			closeFeatureGate();
		}
	}, [location.pathname, closeFeatureGate]);

	useEffect(() => {
		if (!loading && !subscriptionLoading) {
			setIsLoading(false);
		}
	}, [loading, subscriptionLoading]);

	const isAuthenticated = !!token && !!userRole;
	const isBootstrappingSession = !!token && !userRole && loading;
	const companyData = company?.data?.data || company?.data || company;
	const portalUrlPrefix = companyData?.portalUrlPrefix || null;
	const hostRedirectUrl =
		isAuthenticated && token
			? buildAuthenticatedHostRedirectUrl({
					userRole,
					portalUrlPrefix,
					pathname: location.pathname,
					search: location.search,
					token,
					dashboardPath: getDashboardPathForRole(userRole),
				})
			: null;

	const crossAppRedirectUrl =
		!hostRedirectUrl && isAuthenticated && token
			? resolveCrossAppRoleRedirectUrl({
					userRole,
					token,
					pathname: location.pathname,
					search: location.search,
				})
			: null;

	const externalRedirectUrl = hostRedirectUrl || crossAppRedirectUrl;

	useEffect(() => {
		if (externalRedirectUrl) {
			window.location.replace(externalRedirectUrl);
		}
	}, [externalRedirectUrl]);

	if (isClientPortalPath(location.pathname)) {
		return (
			<>
				<PlatformStatusBanner />
				<Suspense fallback={<LoadingSpinner />}>
					<ClientPortalApp />
				</Suspense>
			</>
		);
	}

	if (isLoading || isBootstrappingSession) {
		return <LoadingSpinner />;
	}

	if (externalRedirectUrl) {
		return <LoadingSpinner />;
	}

	if (
		shouldRedirectPortalGuestToAuth({
			hostname: window.location.hostname,
			pathname: location.pathname,
			isAuthenticated,
			isBootstrapping: isBootstrappingSession,
		})
	) {
		return <Navigate to='/auth' replace />;
	}

	// Simple, clean subscription check
	const hasActiveSubscriptionPlan = subscription?.hasActiveSubscription || false;

	// Authenticated company staff need an active subscription.
	const canNavigateTheApplication =
		isAuthenticated && hasActiveSubscriptionPlan;

	// Logged-in users must not stay on marketing / auth pages
	if (
		isAuthenticated &&
		isGuestOnlyPath(location.pathname) &&
		!subscriptionLoading
	) {
		const destination = canNavigateTheApplication
			? getDashboardPathForRole(userRole)
			: hasAppSumoFlow()
				? '/subscribe-to-plan?appsumo=1'
				: '/subscribe-to-plan';
		return <Navigate to={destination} replace />;
	}

	// Only show subscription page if we're not loading subscription data and user doesn't have active plan
	const shouldShowSubscriptionPage =
		isAuthenticated && !subscriptionLoading && !canNavigateTheApplication;

	return (
		<>
			<PlatformStatusBanner />
			{isAuthenticated ? (
				<>
					{canNavigateTheApplication && !subscriptionLoading ? (
						<LoggedInLayout
							pageTitle={pageTitle}
							pageDescription={pageDescription}
							token={token}
							companyId={companyId}
							employeeId={userId}
							showPageHeader={true}>
							<ScrollToTopRef />
							{isFeatureGated ? <FeatureGatePage /> : <AppRoutes />}
						</LoggedInLayout>
					) : shouldShowSubscriptionPage ? (
						<LoggedInLayout
							pageTitle={pageTitle}
							pageDescription={pageDescription}
							token={token}
							companyId={companyId}
							employeeId={userId}
							showPageHeader={true}>
							<ScrollToTopRef />
							<SubscribeToPlanFirstTimePage />
						</LoggedInLayout>
					) : (
						// Show loading spinner while subscription data is being fetched
						<LoadingSpinner />
					)}
				</>
			) : (
				<>
					<LoggedOutLayout>
						<ScrollToTopRef />
						<AppRoutes />
					</LoggedOutLayout>
				</>
			)}
		</>
	);
};

export default App;
